/**
 * DocValidityPanel — "Document validity" on the Documents tab.
 *
 *   ReferenceColumns  one column per reference day (Today / At closing, or At
 *                     close + Today on a closed loan): the date, where it came
 *                     from, whether it sets status, a stacked bar and a legend
 *                     that doubles as the filter. The insight panel renders
 *                     these on their own.
 *   ChangeStrip       what changes between today and closing ("2 lapse before
 *                     closing", "1 enters the 10-day window").
 *   ValidityTimeline  every dated document's window on one shared axis, with
 *                     the Today and closing lines drawn through it.
 *   DocValidityPanel  all of the above as one card — the Document Validity
 *                     sub-tab.
 *
 * No rule lives here — docValidityModel.js shapes the shared helpers (see
 * "Document validity & expiration" in CLAUDE.md).
 */
import { useMemo, useState } from "react";
import { Box, ButtonBase, IconButton, Tooltip } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useDispatch } from "react-redux";

import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import { selectedStateSx } from "../../../../theme/activeStates";
import LoanTabShell from "../../shared/tabLayout/LoanTabShell";
import { TAB_TOKENS as TAB_T } from "../../shared/tabLayout/tabTokens";
import { amber, slate, status, steel, teal } from "../../../../theme/colors";
import {
  STATUSES,
  STATUS_LABEL,
  buildScale,
  countAt,
  dayKey,
  daysText,
  diffDays,
  enteringWindow,
  parseFilter,
  rowMatches,
} from "./docValidityModel";

// The one place a validity status maps to a colour.
export const VALIDITY_TONE = {
  valid: { fg: status.success.solid, bg: status.success.bg },
  expiring: { fg: amber.main, bg: amber.light },
  expired: { fg: status.error.solid, bg: status.error.bg },
  lapse: { fg: status.warning.solid, bg: status.warning.bg },
};

const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const LINE = slate[200];
const LINE_SOFT = slate[100];
const fmtLong = (ms) => formatDate(dayKey(ms), "—", "EEE, MMM d, yyyy");
const fmtShort = (ms) => formatDate(dayKey(ms), "—", "M/d/yy");
const fmtMonth = (ms) => formatDate(dayKey(ms), "", "MMM");
const docName = (doc) =>
  doc?.file_display_name || doc?.fileName || doc?.docType || "Document";

// ── Reference column ────────────────────────────────────────────────────────
const RoleBadge = ({ refDay, today }) => {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: 11.5,
    fontWeight: 500,
    px: "7px",
    py: "1px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
  };
  // Today on a live loan needs no badge — the title already says "as of today".
  if (refDay.role === "sets") return null;
  if (refDay.role === "frozen")
    return (
      <Box sx={{ ...base, background: steel.tint, color: steel.main }}>
        <LockRoundedIcon sx={{ fontSize: 11 }} />
        Loan closed
      </Box>
    );
  const inDays = diffDays(refDay.dayMs, today);
  const label =
    refDay.role === "upcoming"
      ? inDays === 0
        ? "Upcoming · today"
        : `Upcoming · in ${inDays}d`
      : "For reference";
  return (
    <Box sx={{ ...base, border: `1px solid ${LINE}`, color: slate[600] }}>
      {label}
    </Box>
  );
};

// What a column measures, in words: "Document validity as of today" /
// "… as of expected closing date" / "… as of note date".
const columnTitle = (refDay) =>
  `Document validity as of ${refDay.key === "today" ? "today" : (refDay.fieldLabel || "closing").toLowerCase()}`;

// The legend key for a reference line: solid for Today, dashed teal for the anchor.
const MarkerKey = ({ anchor }) => (
  <Box
    aria-hidden
    sx={{
      width: 14,
      height: 0,
      flexShrink: 0,
      borderTop: anchor ? `2px dashed ${teal[500]}` : `2px solid ${slate[600]}`,
    }}
  />
);

// `compact` = the insight panel's variant: no title row, a smaller date, and
// ONE line under it carrying everything the title + badge + source said:
//   "Document validity as of today"
//   "Document validity as of scheduled closing date `closingScheduledDate` · in 8d"
//   "🔒 Document validity as of note date `originationDate` · loan closed"
//   "Document validity as of today · for reference"
// `sourceText` replaces the whole line — the insight panel uses it when both
// days agree ("Document validity as of today and closing (Oct 1)").
const CompactSource = ({ refDay, model, sourceText }) => {
  if (sourceText)
    return (
      <Txt component="div" sx={{ fontSize: 12.5, color: slate[500] }}>
        {sourceText}
      </Txt>
    );
  const inDays = diffDays(refDay.dayMs, model.today);
  const tail =
    refDay.role === "upcoming"
      ? inDays === 0
        ? "today"
        : `in ${inDays}d`
      : refDay.role === "frozen"
        ? "loan closed"
        : refDay.role === "reference"
          ? "for reference"
          : model.unresolved
            ? "no close date on file"
            : null;
  return (
    <Txt
      component="div"
      sx={{
        fontSize: 12.5,
        color: slate[500],
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "4px",
      }}
    >
      {refDay.role === "frozen" && (
        <LockRoundedIcon sx={{ fontSize: 12, color: steel.main }} />
      )}
      <span>{columnTitle(refDay)}</span>
      {refDay.field && (
        <Box
          component="code"
          sx={{
            fontFamily: MONO,
            fontSize: 11.5,
            background: LINE_SOFT,
            color: slate[600],
            px: "5px",
            py: "1px",
            borderRadius: "4px",
          }}
        >
          {refDay.field}
        </Box>
      )}
      {tail && <span>· {tail}</span>}
    </Txt>
  );
};

const ReferenceColumn = ({
  refDay,
  model,
  active,
  onPick,
  divider,
  compact,
  sourceText,
}) => {
  const counts = countAt(model.rows, refDay.key);
  const total = STATUSES.reduce((s, k) => s + counts[k], 0);
  const muted = refDay.role === "reference";
  const clickable = typeof onPick === "function";
  return (
    <Box
      sx={{
        p: "16px 20px 18px",
        minWidth: 0,
        borderLeft: divider ? `1px solid ${LINE}` : "none",
        "@container (max-width: 700px)": {
          borderLeft: "none",
          borderTop: divider ? `1px solid ${LINE}` : "none",
        },
      }}
    >
      {/* Compact drops the title row: the date leads, and the line under it
          says what it measures (see CompactSource). */}
      {!compact && (
        <Flx ac g={1} sx={{ flexWrap: "wrap", mb: 1 }}>
          <MarkerKey anchor={refDay.key === "anchor"} />
          <Txt sx={{ fontWeight: 600, fontSize: 13.5, color: slate[900] }}>
            {columnTitle(refDay)}
          </Txt>
          <RoleBadge refDay={refDay} today={model.today} />
        </Flx>
      )}
      <Txt
        component="div"
        sx={{
          fontSize: compact ? 17 : 20,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: slate[900],
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {fmtLong(refDay.dayMs)}
      </Txt>
      {compact ? (
        <CompactSource refDay={refDay} model={model} sourceText={sourceText} />
      ) : (
        <Txt component="div" sx={{ fontSize: 12.5, color: slate[500] }}>
          {refDay.field ? (
            <>
              From {refDay.fieldLabel.toLowerCase()}{" "}
              <Box
                component="code"
                sx={{
                  fontFamily: MONO,
                  fontSize: 11.5,
                  background: LINE_SOFT,
                  color: slate[600],
                  px: "5px",
                  py: "1px",
                  borderRadius: "4px",
                }}
              >
                {refDay.field}
              </Box>
            </>
          ) : model.unresolved ? (
            "Fallback — no close date on file"
          ) : (
            "System date"
          )}
        </Txt>
      )}

      {/* Stacked bar — each segment filters to its status on this day. */}
      <Flx
        role="group"
        aria-label={`${refDay.name} breakdown`}
        sx={{
          mt: "14px",
          height: 12,
          gap: "2px",
          borderRadius: "6px",
          overflow: "hidden",
          background: LINE_SOFT,
          opacity: muted ? 0.55 : 1,
        }}
      >
        {total > 0 &&
          STATUSES.filter((s) => counts[s]).map((s) => (
            <Tooltip
              key={s}
              arrow
              title={`${counts[s]} ${STATUS_LABEL[s].toLowerCase()} ${refDay.key === "today" ? "today" : `on ${fmtShort(refDay.dayMs)}`}`}
            >
              <ButtonBase
                aria-label={`${counts[s]} ${STATUS_LABEL[s]}`}
                onClick={
                  clickable ? () => onPick(`${refDay.key}:${s}`) : undefined
                }
                sx={{
                  flex: counts[s],
                  minWidth: 6,
                  height: "100%",
                  background: VALIDITY_TONE[s].fg,
                  opacity: active && active !== s ? 0.3 : 1,
                  cursor: clickable ? "pointer" : "default",
                  transition: "opacity .15s, filter .15s",
                  "&:hover": { filter: "brightness(1.08)" },
                }}
              />
            </Tooltip>
          ))}
      </Flx>

      {/* Legend = filters. */}
      <Flx
        sx={{
          flexWrap: "wrap",
          gap: "4px 6px",
          mt: 1.25,
          opacity: muted ? 0.55 : 1,
        }}
      >
        {STATUSES.map((s) => {
          const on = active === s;
          return (
            <ButtonBase
              key={s}
              disabled={!counts[s] || !clickable}
              aria-pressed={on}
              onClick={() => onPick(`${refDay.key}:${s}`)}
              sx={{
                gap: "7px",
                pl: 0.75,
                pr: 1,
                py: 0.5,
                fontSize: 13,
                borderRadius: "6px",
                border: `1px solid ${on ? slate[400] : "transparent"}`,
                background: on ? slate[50] : "transparent",
                color: on ? slate[900] : slate[600],
                "&:hover": { background: slate[50], borderColor: LINE },
                "&.Mui-disabled": {
                  opacity: counts[s] ? 1 : 0.5,
                  color: slate[600],
                },
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: VALIDITY_TONE[s].fg,
                }}
              />
              {STATUS_LABEL[s]}
              <Box
                component="b"
                sx={{
                  color: slate[900],
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {counts[s]}
              </Box>
            </ButtonBase>
          );
        })}
      </Flx>
    </Box>
  );
};

/**
 * The reference columns side by side. `refs` defaults to every reference day;
 * `filter` / `onFilter` are the tab-level filter string (see docValidityModel).
 * `placeholder` fills the second slot when there is only one day to show —
 * the insight panel puts the "no upcoming closing" notice there, where the
 * At closing column would be, instead of above both.
 */
export const ReferenceColumns = ({
  model,
  refs,
  filter,
  onFilter,
  compact,
  placeholder,
  // compact only: replaces the "Document validity as of …" line.
  sourceText,
  sx,
}) => {
  const shown = refs || model.refs;
  const f = parseFilter(filter);
  const fill = shown.length === 1 && placeholder;
  return (
    <Box
      sx={{
        containerType: "inline-size",
        display: "grid",
        gridTemplateColumns: shown.length === 2 || fill ? "1fr 1fr" : "1fr",
        "@container (max-width: 700px)": { gridTemplateColumns: "1fr" },
        ...sx,
      }}
    >
      {shown.map((r, i) => (
        <ReferenceColumn
          key={r.key}
          refDay={r}
          model={model}
          active={
            f && f.refKey === r.key && f.status !== "lapse" ? f.status : null
          }
          onPick={onFilter}
          divider={i > 0}
          compact={compact}
          sourceText={sourceText}
        />
      ))}
      {fill && (
        <Flx
          ac
          sx={{
            p: "16px 20px 18px",
            minWidth: 0,
            borderLeft: `1px solid ${LINE}`,
            "@container (max-width: 700px)": {
              borderLeft: "none",
              borderTop: `1px solid ${LINE}`,
            },
          }}
        >
          {placeholder}
        </Flx>
      )}
    </Box>
  );
};

// ── Change strip ────────────────────────────────────────────────────────────
export const ChangeStrip = ({ model, filter, onFilter, sx }) => {
  const lapses = model.rows.filter((r) => r.lapse).length;
  const entering = enteringWindow(model.rows, model.closed).length;
  const on = filter === "anchor:lapse";
  const chip = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    borderRadius: "999px",
    border: `1px solid ${LINE}`,
    background: "#fff",
    px: "11px",
    pl: "9px",
    py: "3px",
    fontSize: 12.5,
    fontWeight: 500,
  };
  return (
    <Flx
      ac
      sx={{
        flexWrap: "wrap",
        gap: "10px 16px",
        px: "20px",
        py: "11px",
        borderTop: `1px solid ${LINE}`,
        background: slate[50],
        fontSize: 13,
        color: slate[600],
        ...sx,
      }}
    >
      <Txt sx={{ fontSize: 13, color: slate[600] }}>
        Between today and closing
      </Txt>
      {lapses ? (
        <ButtonBase
          aria-pressed={on}
          onClick={onFilter ? () => onFilter("anchor:lapse") : undefined}
          sx={{
            ...chip,
            color: VALIDITY_TONE.lapse.fg,
            borderColor: on ? VALIDITY_TONE.lapse.fg : LINE,
            background: on ? VALIDITY_TONE.lapse.bg : "#fff",
            "&:hover": { borderColor: VALIDITY_TONE.lapse.fg },
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: VALIDITY_TONE.lapse.fg,
            }}
          />
          {lapses} lapse before closing
        </ButtonBase>
      ) : (
        <Box sx={{ ...chip, color: slate[400] }}>Nothing lapses</Box>
      )}
      {entering > 0 && (
        <Box sx={{ ...chip, color: slate[600] }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: VALIDITY_TONE.expiring.fg,
            }}
          />
          {entering} enter the 10-day window
        </Box>
      )}
    </Flx>
  );
};

// ── Notices ─────────────────────────────────────────────────────────────────
export const ValidityNotice = ({ model, sx }) => {
  const warnTone = {
    background: status.warning.bg,
    color: status.warning.fg,
    border: `1px solid ${amber.light}`,
  };
  const infoTone = {
    background: steel.tint,
    color: steel.main,
    border: "1px solid transparent",
  };
  let tone = null;
  let Icon = null;
  let body = null;
  if (model.unresolved) {
    tone = warnTone;
    Icon = WarningAmberRoundedIcon;
    body = (
      <>
        <b>This loan is closed, but no closing date is on file.</b> Validity is
        counted from today until one is added. Add a{" "}
        {model.missingFields.join(", ").toLowerCase()} to freeze it at the
        close.
      </>
    );
  } else if (model.slipped) {
    tone = warnTone;
    Icon = WarningAmberRoundedIcon;
    body = model.slipped.length ? (
      <>
        <b>No upcoming closing date.</b> The{" "}
        {model.slipped
          .map((s) => `${s.label.toLowerCase()} (${fmtShort(s.dayMs)})`)
          .join(" and ")}{" "}
        {model.slipped.length === 1 ? "has" : "have"} already passed. Update it
        to see what lapses before closing.
      </>
    ) : (
      <>
        <b>No closing date yet.</b> Add one to see what lapses before closing.
      </>
    );
  } else if (model.closed) {
    const anchor = model.refs[0];
    tone = infoTone;
    Icon = InfoOutlinedIcon;
    body = (
      <>
        This loan is closed, so validity is frozen on the{" "}
        {anchor.fieldLabel.toLowerCase()}, <b>{fmtLong(anchor.dayMs)}</b>.
        Today&apos;s view is for reference only and doesn&apos;t change document
        status.
      </>
    );
  }
  if (!body) return null;
  return (
    <Flx
      g={1.25}
      sx={{
        p: "10px 12px",
        borderRadius: "7px",
        fontSize: 13,
        lineHeight: 1.45,
        ...tone,
        ...sx,
      }}
    >
      <Icon sx={{ fontSize: 16, flexShrink: 0, mt: "1px" }} />
      <Box>{body}</Box>
    </Flx>
  );
};

// ── Timeline ────────────────────────────────────────────────────────────────
const GRID = {
  display: "grid",
  gridTemplateColumns:
    "minmax(190px,1.3fr) minmax(220px,2fr) minmax(190px,1fr)",
  "@container (max-width: 820px)": { gridTemplateColumns: "1fr auto" },
  "@container (max-width: 520px)": { gridTemplateColumns: "1fr" },
};

const Pill = ({ tone, children, title }) => {
  const t = VALIDITY_TONE[tone];
  const el = (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        fontSize: 12,
        fontWeight: 500,
        px: 1,
        py: "2px",
        borderRadius: "999px",
        background: t.bg,
        color: t.fg,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
  return title ? (
    <Tooltip arrow title={title}>
      {el}
    </Tooltip>
  ) : (
    el
  );
};

export const ValidityTimeline = ({
  model,
  lens = "compare",
  filter,
  onClearFilter,
  onOpenDoc,
}) => {
  const { rows, refs } = model;
  const scale = useMemo(() => buildScale(rows, refs), [rows, refs]);
  const pillRefs =
    lens === "compare" ? refs : refs.filter((r) => r.key === lens);
  // The window bar is coloured by the lens; in compare, by the anchor (the
  // actionable day) when there is one.
  const colorKey =
    lens !== "compare"
      ? lens
      : (refs.find((r) => r.key === "anchor") || refs[0]).key;
  const f = parseFilter(filter);
  const visible = rows.filter((r) => rowMatches(r, filter));
  const filterLabel = !f
    ? ""
    : f.status === "lapse"
      ? "Lapse before closing"
      : f.status === "missingStart"
        ? "Missing a start date"
        : `${STATUS_LABEL[f.status]} · ${(refs.find((r) => r.key === f.refKey)?.name || "").toLowerCase()}`;

  if (!scale) return null;
  return (
    <Box sx={{ containerType: "inline-size", borderTop: `1px solid ${LINE}` }}>
      <Flx
        ac
        jb
        sx={{ flexWrap: "wrap", gap: "8px 16px", px: "20px", py: 1.5 }}
      >
        <Txt sx={{ fontSize: 13, fontWeight: 600, color: slate[900] }}>
          {f
            ? `${visible.length} of ${rows.length} documents`
            : "Validity windows"}
        </Txt>
        {f ? (
          <Flx
            ac
            g={0.75}
            sx={{
              fontSize: 12.5,
              fontWeight: 500,
              pl: 1.25,
              pr: 0.5,
              py: "3px",
              borderRadius: "999px",
              background: steel.tint,
              color: steel.main,
            }}
          >
            {filterLabel}
            {onClearFilter && (
              <IconButton
                size="small"
                aria-label="Clear filter"
                onClick={onClearFilter}
                sx={{ p: "2px", color: "inherit" }}
              >
                <CloseRoundedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Flx>
        ) : (
          <Txt sx={{ fontSize: 13, color: slate[500] }}>
            Sorted by expiration · click a segment to filter
          </Txt>
        )}
      </Flx>

      {/* Axis: month ticks + the reference-day labels. */}
      <Box
        sx={{
          ...GRID,
          px: "20px",
          pb: "6px",
          fontSize: 11.5,
          color: slate[500],
          "@container (max-width: 820px)": { display: "none" },
        }}
        aria-hidden
      >
        <Box />
        <Box sx={{ position: "relative", height: 30 }}>
          {scale.ticks.map((t) => (
            <Box
              key={t}
              component="span"
              sx={{
                position: "absolute",
                bottom: 0,
                left: scale.pct(t),
                transform: "translateX(-50%)",
                whiteSpace: "nowrap",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: "50%",
                  bottom: "-6px",
                  height: 5,
                  borderLeft: `1px solid ${LINE}`,
                },
              }}
            >
              {fmtMonth(t)}
            </Box>
          ))}
          {refs.map((r) => (
            <Box
              key={r.key}
              component="span"
              sx={{
                position: "absolute",
                top: 0,
                left: scale.pct(r.dayMs),
                transform: "translateX(-50%)",
                fontSize: 11,
                fontWeight: 600,
                whiteSpace: "nowrap",
                px: "6px",
                py: "1px",
                borderRadius: "4px",
                background: "#fff",
                color: r.key === "today" ? slate[900] : teal[700],
              }}
            >
              {r.key === "today" ? "Today" : fmtShort(r.dayMs)}
            </Box>
          ))}
        </Box>
        <Box />
      </Box>

      {visible.map((row) => {
        const winTone = VALIDITY_TONE[row.at[colorKey]];
        return (
          <Box
            key={row.doc?._id}
            sx={{
              ...GRID,
              px: "20px",
              alignItems: "center",
              minHeight: 56,
              borderTop: `1px solid ${LINE_SOFT}`,
              "&:hover": { background: slate[50] },
            }}
          >
            <Box sx={{ py: "9px", pr: 2, minWidth: 0 }}>
              <ButtonBase
                onClick={onOpenDoc ? () => onOpenDoc(row.doc) : undefined}
                title={docName(row.doc)}
                sx={{
                  display: "block",
                  maxWidth: "100%",
                  textAlign: "left",
                  fontSize: 14,
                  fontWeight: 500,
                  color: slate[900],
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  "&:hover": { color: teal[700], textDecoration: "underline" },
                }}
              >
                {docName(row.doc)}
              </ButtonBase>
              <Txt
                component="div"
                sx={{
                  fontSize: 12,
                  color: slate[500],
                  mt: "1px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {row.doc?.docType || "Document"} ·{" "}
                <Box
                  component="span"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {fmtShort(row.effMs)} → {fmtShort(row.expMs)}
                </Box>{" "}
                ·{" "}
                {row.days === "YEAR_END"
                  ? "valid to year end"
                  : `${row.days}-day window`}
              </Txt>
            </Box>

            <Tooltip
              arrow
              title={`Valid ${fmtLong(row.effMs)} to ${fmtLong(row.expMs)}`}
            >
              <Box
                sx={{
                  position: "relative",
                  height: 40,
                  "@container (max-width: 820px)": {
                    gridColumn: "1 / -1",
                    gridRow: 2,
                    height: 26,
                    mb: 1.25,
                  },
                  "@container (max-width: 520px)": { gridRow: "auto" },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    height: 8,
                    transform: "translateY(-50%)",
                    borderRadius: "4px",
                    left: scale.pct(row.effMs),
                    width: `calc(${scale.pct(row.expMs)} - ${scale.pct(row.effMs)})`,
                    background: winTone.bg,
                    boxShadow: `inset 0 0 0 1px ${winTone.fg}`,
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      right: "-1px",
                      top: "-4px",
                      bottom: "-4px",
                      width: 3,
                      borderRadius: "2px",
                      background: winTone.fg,
                    },
                  }}
                />
                {refs.map((r) => (
                  <Box
                    key={r.key}
                    sx={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      width: 0,
                      left: scale.pct(r.dayMs),
                      borderLeft:
                        r.key === "anchor"
                          ? `1.5px dashed ${teal[500]}`
                          : `1.5px solid ${slate[600]}`,
                      opacity: r.key === "anchor" ? 1 : 0.75,
                    }}
                  />
                ))}
              </Box>
            </Tooltip>

            <Flx
              ac
              sx={{
                flexWrap: "wrap",
                gap: "6px",
                py: 1,
                pl: 2,
                justifyContent: "flex-end",
                "@container (max-width: 520px)": {
                  justifyContent: "flex-start",
                  pl: 0,
                  pt: 0,
                },
              }}
            >
              {row.lapse && lens !== "today" && (
                <Pill tone="lapse">
                  <WarningAmberRoundedIcon sx={{ fontSize: 12 }} />
                  Before closing
                </Pill>
              )}
              {pillRefs.map((r, i) => (
                <Flx ac g={0.75} key={r.key}>
                  {i > 0 && (
                    <Txt aria-hidden sx={{ fontSize: 12, color: slate[400] }}>
                      →
                    </Txt>
                  )}
                  <Pill
                    tone={row.at[r.key]}
                    title={`${r.name} (${fmtShort(r.dayMs)}): ${STATUS_LABEL[row.at[r.key]]}, ${daysText(row, r.key)}`}
                  >
                    {lens === "compare"
                      ? daysText(row, r.key)
                      : `${STATUS_LABEL[row.at[r.key]]} · ${daysText(row, r.key)}`}
                  </Pill>
                </Flx>
              ))}
            </Flx>
          </Box>
        );
      })}
      {!visible.length && (
        <Box
          sx={{
            py: "26px",
            px: "20px",
            textAlign: "center",
            color: slate[500],
            fontSize: 13,
            borderTop: `1px solid ${LINE_SOFT}`,
          }}
        >
          No documents match this filter.
          {onClearFilter && (
            <ButtonBase
              onClick={onClearFilter}
              sx={{
                ml: 0.75,
                fontSize: 13,
                fontWeight: 500,
                color: teal[700],
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Show all
            </ButtonBase>
          )}
        </Box>
      )}
    </Box>
  );
};

// ── Lens toggle ─────────────────────────────────────────────────────────────
// `withCompare` adds the "Compare" option (the sub-tab); `small` is the
// insight panel's size. Exported for the panel's Today / At closing switch.
// Full size matches the tab-header buttons (TabGhostButton: 12.5/600,
// 8px 13px, strong border) so it lines up with Email / Upload elsewhere; the
// selected option takes the brand teal tint + teal-700 label.
export const LensToggle = ({ refs, value, onChange, withCompare = true, small = false }) => (
  <Flx
    role="group"
    aria-label="Validity as of"
    sx={{
      border: `1px solid ${small ? LINE : TAB_T.color.borderStrong}`,
      borderRadius: small ? "7px" : TAB_T.radius.xs,
      overflow: "hidden",
      flexShrink: 0,
      background: "#fff",
    }}
  >
    {[...(withCompare ? [{ key: "compare", name: "Compare" }] : []), ...refs].map((o, i) => {
      const selected = value === o.key;
      const divider = i
        ? `1px solid ${small ? LINE : TAB_T.color.borderStrong}`
        : "none";
      return (
        <ButtonBase
          key={o.key}
          aria-pressed={selected}
          onClick={() => onChange(o.key)}
          sx={{
            px: small ? 1 : "13px",
            py: small ? "3px" : "8px",
            fontSize: small ? 11.5 : 12.5,
            fontWeight: small ? 400 : 600,
            lineHeight: small ? undefined : 1.75,
            whiteSpace: "nowrap",
            color: slate[600],
            background: "#fff",
            borderLeft: divider,
            "&:hover": { background: teal[50], color: teal[700] },
            ...(selected
              ? {
                  ...selectedStateSx({ level: 1 }),
                  borderLeft: divider,
                  "&:hover": { background: teal[100], color: teal[700] },
                }
              : null),
          }}
        >
          {o.name}
        </ButtonBase>
      );
    })}
  </Flx>
);

/**
 * The full card (the Document Validity sub-tab). `filter` / `onFilter` may be
 * passed to control the filter from outside; otherwise it keeps its own.
 * `onOpenInTable(filter)` sends the current filter to the documents table.
 */
const DocValidityPanel = ({
  model,
  filter: filterProp,
  onFilter: onFilterProp,
  onOpenInTable,
  // The Document Validity sub-tab: the header (title, counts line, day
  // toggle) becomes the LoanTabShell page header — the same frame the
  // Contacts tab uses — and the card below holds only the body.
  shell = false,
}) => {
  const dispatch = useDispatch();
  const [lens, setLens] = useState("compare");
  const [localFilter, setLocalFilter] = useState(null);
  const controlled = onFilterProp !== undefined;
  const filter = controlled ? filterProp : localFilter;
  const setFilter = controlled ? onFilterProp : setLocalFilter;
  const toggle = (next) => setFilter(filter === next ? null : next);

  const { refs, rows } = model;
  const two = refs.length === 2;
  const activeLens = two ? lens : refs[0].key;
  const shownRefs =
    activeLens === "compare" ? refs : refs.filter((r) => r.key === activeLens);
  const changeLens = (next) => {
    setLens(next);
    const f = parseFilter(filter);
    if (f && next !== "compare" && f.refKey && f.refKey !== next)
      setFilter(null);
  };
  const openDoc = (doc) => {
    if (doc) dispatch(filePreviewSetData(doc));
  };
  const missingStart = model.missingStartIds.length;

  const counts = (
    <>
            {rows.length} with a validity window · {model.undated} don&apos;t
            expire
            {missingStart > 0 && (
              <>
                {" · "}
                <ButtonBase
                  onClick={
                    onOpenInTable
                      ? () => onOpenInTable("missingStart")
                      : undefined
                  }
                  sx={{
                    font: "inherit",
                    fontWeight: 500,
                    color: teal[700],
                    verticalAlign: "baseline",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {missingStart} missing a start date
                </ButtonBase>
              </>
            )}
    </>
  );
  const dayToggle = two ? (
    <LensToggle refs={refs} value={activeLens} onChange={changeLens} />
  ) : null;

  const card = (
    <Box
      sx={{
        background: "#fff",
        border: `1px solid ${LINE}`,
        borderRadius: "10px",
        boxShadow:
          "0 1px 2px rgba(20,30,45,.06), 0 4px 14px rgba(20,30,45,.05)",
      }}
    >
      {!shell && (
        <>
      <Flx
        sx={{
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px 20px",
          p: "18px 20px 14px",
          alignItems: "flex-start",
        }}
      >
        <Flx column g={0.5}>
          <Txt
            component="div"
            sx={{ fontSize: 16, fontWeight: 600, color: slate[900] }}
          >
            Document validity
          </Txt>
          <Txt component="div" sx={{ fontSize: 13, color: slate[500] }}>
            {counts}
          </Txt>
        </Flx>
        {dayToggle}
      </Flx>

        </>
      )}
      <ValidityNotice model={model} sx={{ mx: "20px", mb: "14px", mt: shell ? "16px" : 0 }} />

      {rows.length ? (
        <>
          <ReferenceColumns
            model={model}
            refs={shownRefs}
            filter={filter}
            onFilter={toggle}
            // In the shell the card has no header row, so the columns ARE its
            // top edge — a divider here would double the card's own border.
            sx={shell ? undefined : { borderTop: `1px solid ${LINE}` }}
          />
          {two && !model.closed && activeLens === "compare" && (
            <ChangeStrip model={model} filter={filter} onFilter={toggle} />
          )}
          <ValidityTimeline
            model={model}
            lens={activeLens}
            filter={filter}
            onClearFilter={() => setFilter(null)}
            onOpenDoc={openDoc}
          />
        </>
      ) : (
        <Box
          sx={{
            py: 4,
            px: "20px",
            textAlign: "center",
            color: slate[500],
            fontSize: 13,
            borderTop: shell ? "none" : `1px solid ${LINE}`,
          }}
        >
          No documents on this loan have a validity window yet.
        </Box>
      )}

      <Flx
        sx={{
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          p: "11px 20px",
          borderTop: `1px solid ${LINE}`,
        }}
      >
        <Txt sx={{ fontSize: 12.5, color: slate[500] }}>
          Expiring soon = 10 days or less. Windows come from the document type.
        </Txt>
        {onOpenInTable && (
          <ButtonBase
            onClick={() => onOpenInTable(filter)}
            sx={{
              fontSize: 12.5,
              fontWeight: 500,
              color: teal[700],
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Open in documents table
          </ButtonBase>
        )}
      </Flx>
    </Box>
  );
  if (!shell) return card;
  return (
    <LoanTabShell title="Document validity" subTitle={counts} endContent={dayToggle}>
      {card}
    </LoanTabShell>
  );
};

export default DocValidityPanel;
